"use client";

import Image from "next/image";

import { CMATitle, MDTitle, NPTitle, PHDTitle, RNTitle } from "../svgs/titles";

interface TeamCardProps {
    member: TeamMember;
    onSelect: (member: TeamMember) => void;
    className?: string;
}

// Helper function to get the appropriate SVG component based on degree path
const getDegreeComponent = (degreePath: string, color: string) => {
    if (!degreePath) return null;

    const degreeType = degreePath.split("/").pop()?.replace(".png", "");

    switch (degreeType) {
        case "PHD":
            return <PHDTitle color={color} className="w-full h-auto" />;
        case "MD":
        case "MD1":
            return <MDTitle color={color} className="w-full h-auto" />;
        case "CMA":
            return <CMATitle color={color} className="w-full h-auto" />;
        case "RN":
            return <RNTitle color={color} className="w-full h-auto" />;
        case "NP":
            return <NPTitle color={color} className="w-full h-auto" />;
        default:
            return null;
    }
};

// Helper function to get text color based on background
const getTextColor = (bgColor: string) => {
    // Extract the background color and determine appropriate text color
    if (bgColor.includes("dark:bg-")) {
        // For dark mode, use white text
        return "#ffffff";
    }
    // For light backgrounds, use dark text
    return "#333333";
};

export default function TeamCard({
    member,
    onSelect,
    className = "",
}: TeamCardProps) {
    return (
        <div
            className={`w-[300px] transition-all duration-300 ease-out hover:translate-y-[-7px] hover:shadow-lg overflow-hidden rounded-2xl cursor-pointer ${className}`}
            onClick={() => onSelect(member)}
        >
            <div className={`relative rounded-2xl h-full ${member.bgColor}`}>
                {member.degree && (
                    <div className={`absolute top-4 right-6 w-[70px]`}>
                        {getDegreeComponent(
                            member.degree,
                            getTextColor(member.bgColor),
                        )}
                    </div>
                )}

                <div className="hidden md:block p-8">
                    <div className="flex flex-col gap-4">
                        <div className="relative w-36 h-36">
                            <Image
                                src={member.image}
                                alt={member.name}
                                width={144}
                                height={176}
                                className="w-full h-full object-cover object-bottom relative z-10"
                            />
                        </div>
                        <div className="flex flex-col gap-5">
                            <h3 className="text-[#333333] dark:text-white text-2xl font-medium font-satoshi leading-relaxed">
                                {member.name}
                            </h3>
                            <p className="text-[#333333] dark:text-gray-200 text-base font-light font-space leading-snug">
                                {member.title}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="md:hidden p-4">
                    <div className="flex flex-row gap-6">
                        <div className="relative w-28 h-28">
                            <Image
                                src={member.image}
                                alt={member.name}
                                width={144}
                                height={176}
                                className="w-full h-full object-cover object-bottom relative z-10"
                            />
                        </div>
                        <div className="flex flex-col justify-center w-1/2 mt-6">
                            <h3 className="text-[#333333] dark:text-white text-xl font-medium font-satoshi">
                                {member.name}
                            </h3>
                            <p className="text-[#333333] dark:text-gray-200 text-sm font-light font-space mt-1">
                                {member.title}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
